import React, { useEffect, useState } from 'react'

import GoToBox from './GoToBox'
import { parseGoTo, type GoToResult } from './goTo'
import { useLocusList } from './useLocusList'

type LocusListPanelProps = {
  caseId: string
  /** Same handler the search box uses, so a saved entry navigates identically. */
  onGo: (result: GoToResult) => void
  /** Live free-text from the search box, for table filtering. */
  onLiveText: (text: string) => void
}

const EXAMPLE =
  'Enter loci, intervals, or variant ids - one per line or space-separated.\nFor example:\nchr1:55,516,888\nchr2:152341851-152591002\nchr1-12345-A-G\n2-239176431-T-G\nchr2(GRCh38):g.239176431T>G\nrs113993960'

// Split a pasted blob into individual entries. We split on whitespace/newlines
// (not commas) so comma thousands-separators inside a locus (chr1:55,516,888)
// survive; any stray surrounding commas are then trimmed off each token.
function splitEntries(raw: string): string[] {
  return raw
    .split(/[\s;]+/)
    .map((t) => t.replace(/^,+|,+$/g, '').trim())
    .filter(Boolean)
}

/**
 * TGG-style "Locus list" for the left sidebar (GEN-3103): the go-to search box
 * plus a saved, clickable list of loci that persists per case in the browser.
 */
const LocusListPanel: React.FC<LocusListPanelProps> = ({ caseId, onGo, onLiveText }) => {
  const { items, addMany, remove, clear } = useLocusList(caseId)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAdding(false)
    }
    if (adding) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [adding])

  const save = () => {
    const entries = splitEntries(draft)
    if (entries.length) addMany(entries)
    setDraft('')
    setAdding(false)
  }

  return (
    <div className="border-border-subtle border-b p-4" data-testid="locus-list-panel">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-text-heading text-sm font-semibold">Locus list</h2>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-brand-700 text-xs hover:underline"
          data-testid="locus-add-open"
        >
          Add loci
        </button>
      </div>

      <GoToBox onGo={onGo} onLiveText={onLiveText} />

      {items.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1" data-testid="locus-list">
          {items.map((entry) => (
            <li key={entry} className="group flex items-center gap-1">
              <button
                type="button"
                onClick={() => onGo(parseGoTo(entry))}
                className="text-text-subtle hover:bg-brand-50 hover:text-brand-800 min-w-0 flex-1 truncate rounded bg-gray-100 px-2 py-1 text-left font-mono text-[11px]"
                data-testid="locus-chip"
                title={`Go to ${entry}`}
              >
                {entry}
              </button>
              <button
                type="button"
                onClick={() => remove(entry)}
                className="text-text-subtle hover:text-text-heading shrink-0 px-1 text-xs"
                aria-label={`Remove ${entry}`}
              >
                x
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-text-subtle mt-3 text-[11px]">No saved loci yet. Use "Add loci" to build a quick list.</p>
      )}

      {items.length > 0 && (
        <button
          type="button"
          onClick={clear}
          className="text-text-subtle hover:text-text-heading mt-2 text-[11px] hover:underline"
          data-testid="locus-clear"
        >
          Clear all
        </button>
      )}

      {adding && (
        <div
          // z-index above igv.js's own elements (it goes up to 99999), so its
          // track text/popovers don't paint over our modal.
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setAdding(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Add loci to the list"
            className="w-full max-w-lg rounded-lg bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-text-heading mb-2 text-sm font-semibold">Add loci to the list</h3>
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={EXAMPLE}
              rows={6}
              className="border-border-subtle focus:border-brand-500 w-full rounded border px-2 py-2 font-mono text-xs outline-none"
              data-testid="locus-add-textarea"
            />
            <p className="text-text-subtle mt-2 text-[11px]">
              Saved in this browser only; not included when you share the link.
            </p>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="border-border-subtle text-text-subtle hover:text-text-heading rounded border px-3 py-1 text-xs"
                data-testid="locus-add-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                className="bg-brand-600 hover:bg-brand-700 rounded px-3 py-1 text-xs font-medium text-white"
                data-testid="locus-add-save"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LocusListPanel
